import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Sparkles, Users, Utensils, ShoppingBag, Plus, Flame } from "lucide-react";
import { toast } from "sonner";
import { FEASTS, findFeast } from "../data/combos";
import { useMenu } from "../context/MenuContext";
import { useCart } from "../context/CartContext";
import { itemTakesSpice, SPICE_LEVELS, DEFAULT_SPICE, spiceLabel } from "../components/VariantPicker";

export default function Feasts() {
  const { slug } = useParams();
  if (slug) return <FeastWizard slug={slug} />;
  return <FeastsIndex />;
}

function FeastsIndex() {
  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="feasts-index">
      <div className="max-w-3xl">
        <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#C9853E] font-bold">
          <Sparkles className="w-3.5 h-3.5" /> Curated Combos
        </div>
        <h1 className="font-display text-4xl md:text-6xl font-extrabold text-[#0E0E1A] leading-[1.05] mt-4">
          Pick a feast.<br />We'll guide the rest.
        </h1>
        <p className="text-[#57534E] mt-5 text-lg leading-relaxed">
          Can't decide? Start with a feast — a hand-picked path through our menu
          for couples, families, and the whole office. We walk you through each
          pick and load your cart when you're done.
        </p>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="feasts-grid">
        {FEASTS.map((f) => (
          <Link
            key={f.slug}
            to={`/feasts/${f.slug}`}
            data-testid={`feast-card-${f.slug}`}
            className="group relative rounded-3xl overflow-hidden border border-[#E7E5E4] hover:border-[#C9853E] transition-all bg-white shadow-sm hover:shadow-xl hover:-translate-y-1"
          >
            <div className="aspect-[4/3] overflow-hidden bg-[#F5F0E8]">
              <img
                src={f.hero}
                alt={f.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            </div>
            <div className="absolute top-4 left-4 flex items-center gap-1.5 bg-white/95 backdrop-blur px-3 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase text-[#0E0E1A]">
              <Users className="w-3 h-3" /> Serves {f.serves}
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <div className="font-display text-2xl md:text-3xl font-extrabold leading-tight">
                {f.name}
              </div>
              <div className="text-sm text-white/85 mt-1.5 leading-snug">{f.tagline}</div>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#E5B07D]">
                  {f.priceHint}
                </span>
                <span className="inline-flex items-center gap-1.5 text-sm font-bold">
                  Build it <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-14 text-sm text-[#57534E] border-t border-[#E7E5E4] pt-6 flex items-center gap-2">
        <Utensils className="w-4 h-4" />
        Prefer to browse à la carte? <Link to="/menu" className="text-[#C9853E] font-bold hover:underline" data-testid="browse-menu-link">See the full menu →</Link>
      </div>
    </div>
  );
}

function FeastWizard({ slug }) {
  const feast = findFeast(slug);
  const { categories, loading } = useMenu();
  const { add, setOpen: setCartOpen } = useCart();
  const navigate = useNavigate();

  const [stepIdx, setStepIdx] = useState(0);
  const [picks, setPicks] = useState({}); // { [stepIdx]: { item, variant } }

  const step = feast?.steps[stepIdx];
  const categoryItems = useMemo(() => {
    if (!step) return [];
    const c = categories.find((c) => c.slug === step.category_slug);
    return (c?.items || []).filter((i) => i.price != null || (i.variants && i.variants.length > 0));
  }, [categories, step]);

  const runningTotal = useMemo(() => {
    return Object.values(picks).reduce((s, pk) => {
      if (!pk) return s;
      const price = pk.variant ? pk.variant.price : pk.item.price || 0;
      return s + price;
    }, 0);
  }, [picks]);

  if (!feast) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="feast-not-found">
        <h1 className="font-display text-3xl font-extrabold">Feast not found</h1>
        <Link to="/feasts" className="mt-6 inline-block text-[#C9853E] font-bold">
          Back to feasts
        </Link>
      </div>
    );
  }

  const selectPick = (item) => {
    let variant = null;
    if (item.variants && item.variants.length > 0) {
      const pref = (step.preferred_variant || "").toLowerCase();
      variant =
        (pref &&
          item.variants.find((v) =>
            (v.name || "").toLowerCase().includes(pref)
          )) ||
        item.variants[0];
    }
    const spice = itemTakesSpice(item) ? DEFAULT_SPICE : null;
    setPicks((p) => ({ ...p, [stepIdx]: { item, variant, spice } }));
  };

  const changeVariant = (variantName) => {
    setPicks((p) => {
      const cur = p[stepIdx];
      if (!cur) return p;
      const variant = cur.item.variants.find((v) => v.name === variantName) || cur.variant;
      return { ...p, [stepIdx]: { ...cur, variant } };
    });
  };

  const changeSpice = (spiceId) => {
    setPicks((p) => {
      const cur = p[stepIdx];
      if (!cur) return p;
      return { ...p, [stepIdx]: { ...cur, spice: spiceId } };
    });
  };

  const pickedCount = Object.keys(picks).length;
  const allSelected = pickedCount === feast.steps.length;

  const next = () => {
    if (!picks[stepIdx]) {
      toast.error("Pick one item to continue");
      return;
    }
    if (stepIdx < feast.steps.length - 1) setStepIdx(stepIdx + 1);
  };
  const prev = () => stepIdx > 0 && setStepIdx(stepIdx - 1);
  const jumpTo = (idx) => setStepIdx(idx);

  const addFeastToCart = () => {
    if (!allSelected) {
      toast.error("Finish all the steps to add your feast");
      return;
    }
    Object.values(picks).forEach((pk) => {
      add({
        item_id: pk.item.id,
        variant_name: pk.variant?.name || null,
        variant_label: pk.variant?.name || null,
        name: pk.item.name,
        price: pk.variant ? pk.variant.price : pk.item.price,
        quantity: 1,
        spice_level: pk.spice || null,
      });
    });
    toast.success(`${feast.name} added to cart`);
    setCartOpen(true);
    navigate("/order");
  };

  const currentPick = picks[stepIdx];

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-16" data-testid={`feast-wizard-${slug}`}>
      <Link to="/feasts" data-testid="feast-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-[#57534E] hover:text-[#C9853E] mb-6">
        <ArrowLeft className="w-4 h-4" /> All feasts
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Main wizard column */}
        <div className="lg:col-span-8">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#C9853E] font-bold">
            <Sparkles className="w-3 h-3" /> {feast.name}
          </div>
          <h1 className="font-display text-3xl md:text-5xl font-extrabold text-[#0E0E1A] mt-3 leading-tight">
            {step.title}
          </h1>
          <p className="text-[#57534E] mt-2 text-base">{step.hint}</p>

          {/* Progress dots */}
          <div className="mt-6 flex items-center gap-2 overflow-x-auto pb-1" data-testid="feast-progress">
            {feast.steps.map((s, i) => {
              const done = !!picks[i];
              const active = i === stepIdx;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => jumpTo(i)}
                  data-testid={`feast-step-${i}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold tracking-wide uppercase transition-all whitespace-nowrap border ${
                    active
                      ? "bg-[#0E0E1A] text-white border-[#0E0E1A]"
                      : done
                      ? "bg-[#C9853E]/10 text-[#A6692D] border-[#C9853E]/30"
                      : "bg-white text-[#57534E] border-[#E7E5E4] hover:border-[#0E0E1A]/40"
                  }`}
                >
                  {done && <Check className="w-3 h-3" />}
                  Step {i + 1}
                </button>
              );
            })}
          </div>

          {/* Choices */}
          {loading ? (
            <div className="mt-10 text-[#57534E]">Loading menu…</div>
          ) : categoryItems.length === 0 ? (
            <div className="mt-10 text-[#57534E]" data-testid="feast-no-items">
              No items available in that category right now — pick a different feast.
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="feast-choices">
              {categoryItems.map((item) => {
                const selected = currentPick?.item.id === item.id;
                const displayPrice =
                  item.price != null
                    ? `$${item.price.toFixed(2)}`
                    : item.variants?.[0]
                    ? `from $${item.variants[0].price.toFixed(2)}`
                    : "";
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => selectPick(item)}
                    data-testid={`feast-choice-${item.id}`}
                    className={`group text-left rounded-2xl p-4 transition-all border-2 ${
                      selected
                        ? "border-[#C9853E] bg-[#C9853E]/5 shadow-md"
                        : "border-[#E7E5E4] bg-white hover:border-[#0E0E1A]/40"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {item.image_data && (
                        <img
                          src={item.image_data}
                          alt={item.name}
                          className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="font-display font-bold text-[#0E0E1A] leading-snug">
                            {item.name}
                          </div>
                          <div className="text-sm font-bold text-[#C9853E] whitespace-nowrap">
                            {displayPrice}
                          </div>
                        </div>
                        {item.desc && (
                          <div className="text-xs text-[#57534E] mt-1 line-clamp-2">
                            {item.desc}
                          </div>
                        )}
                        {selected && (
                          <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-[#A6692D] uppercase tracking-wider">
                            <Check className="w-3 h-3" /> Selected
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Variant picker inline */}
          {currentPick?.item.variants && currentPick.item.variants.length > 1 && (
            <div className="mt-6" data-testid="feast-variant-picker">
              <div className="text-xs tracking-wider uppercase font-bold text-[#57534E] mb-2">
                Size
              </div>
              <div className="flex flex-wrap gap-2">
                {currentPick.item.variants.map((v) => {
                  const active = currentPick.variant?.name === v.name;
                  return (
                    <button
                      key={v.name}
                      type="button"
                      onClick={() => changeVariant(v.name)}
                      data-testid={`feast-variant-${v.name}`}
                      className={`rounded-full px-4 py-2 text-sm font-bold transition-all border ${
                        active
                          ? "bg-[#0E0E1A] text-white border-[#0E0E1A]"
                          : "bg-white text-[#0E0E1A] border-[#E7E5E4] hover:border-[#0E0E1A]"
                      }`}
                    >
                      {v.name} · ${v.price.toFixed(2)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Spice picker inline */}
          {currentPick && itemTakesSpice(currentPick.item) && (
            <div className="mt-6" data-testid="feast-spice-picker">
              <div className="text-xs tracking-wider uppercase font-bold text-[#57534E] mb-2 inline-flex items-center gap-1.5">
                <Flame className="w-3.5 h-3.5 text-[#C9853E]" /> Spice level
              </div>
              <div className="flex flex-wrap gap-2">
                {SPICE_LEVELS.map((lvl) => {
                  const active = currentPick.spice === lvl.id;
                  return (
                    <button
                      key={lvl.id}
                      type="button"
                      onClick={() => changeSpice(lvl.id)}
                      data-testid={`feast-spice-${lvl.id}`}
                      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-all border ${
                        active
                          ? "bg-[#C9853E] text-white border-[#C9853E]"
                          : "bg-white text-[#0E0E1A] border-[#E7E5E4] hover:border-[#C9853E]"
                      }`}
                    >
                      <span className="flex">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Flame
                            key={i}
                            className={`w-3 h-3 ${
                              i < lvl.flames
                                ? active ? "text-white fill-white" : "text-[#C9853E] fill-[#C9853E]"
                                : active ? "text-white/40" : "text-[#E7E5E4]"
                            }`}
                          />
                        ))}
                      </span>
                      {lvl.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Nav buttons */}
          <div className="mt-10 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={prev}
              disabled={stepIdx === 0}
              data-testid="feast-prev-btn"
              className="inline-flex items-center gap-2 text-sm font-bold text-[#57534E] disabled:opacity-40 hover:text-[#0E0E1A]"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>

            {stepIdx < feast.steps.length - 1 ? (
              <button
                type="button"
                onClick={next}
                data-testid="feast-next-btn"
                className="inline-flex items-center gap-2 bg-[#0E0E1A] hover:bg-[#1C1B2B] text-white rounded-full px-6 py-3 font-bold text-sm transition-all"
              >
                Next <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={addFeastToCart}
                disabled={!allSelected}
                data-testid="feast-add-to-cart-btn"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#C9853E] to-[#E5B07D] hover:from-[#A6692D] hover:to-[#C9853E] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-full px-7 py-3.5 font-bold text-sm transition-all shadow-[0_10px_25px_-8px_rgba(201,133,62,0.6)]"
              >
                <ShoppingBag className="w-4 h-4" />
                Add feast to cart · ${runningTotal.toFixed(2)}
              </button>
            )}
          </div>
        </div>

        {/* Summary rail */}
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-28 rounded-3xl p-6 text-white relative overflow-hidden" style={{ background: `linear-gradient(160deg, #0E0E1A 0%, ${feast.accent} 180%)` }}>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B07D] font-bold">
              Your feast
            </div>
            <div className="font-display text-2xl font-extrabold mt-1">{feast.name}</div>
            <div className="text-sm text-white/70 mt-1">Serves {feast.serves}</div>

            <ul className="mt-5 space-y-3" data-testid="feast-summary-list">
              {feast.steps.map((s, i) => {
                const pk = picks[i];
                const active = i === stepIdx;
                return (
                  <li
                    key={i}
                    onClick={() => jumpTo(i)}
                    className={`cursor-pointer rounded-xl px-3 py-2.5 transition-all ${
                      active ? "bg-white/15" : "hover:bg-white/5"
                    }`}
                  >
                    <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-white/60 font-bold">
                      Step {i + 1}
                    </div>
                    {pk ? (
                      <div className="flex items-baseline justify-between gap-2 mt-0.5">
                        <div className="font-bold leading-snug">
                          {pk.item.name}
                          {pk.variant && (
                            <span className="text-white/70 text-xs font-normal"> · {pk.variant.name}</span>
                          )}
                          {pk.spice && pk.spice !== DEFAULT_SPICE && (
                            <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider text-[#E5B07D]">
                              🌶 {spiceLabel(pk.spice)}
                            </span>
                          )}
                        </div>
                        <div className="text-[#E5B07D] text-sm font-bold whitespace-nowrap">
                          ${(pk.variant ? pk.variant.price : pk.item.price).toFixed(2)}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-white/60 mt-0.5 inline-flex items-center gap-1">
                        <Plus className="w-3 h-3" /> {s.title}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="border-t border-white/10 mt-5 pt-4 flex items-baseline justify-between">
              <span className="text-sm text-white/70">Running total</span>
              <span className="font-display text-3xl font-extrabold text-[#E5B07D]" data-testid="feast-running-total">
                ${runningTotal.toFixed(2)}
              </span>
            </div>
            <div className="text-[11px] text-white/50 mt-1">
              {pickedCount}/{feast.steps.length} picked
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
